/** @jsx jsx */
import { ReactNode } from 'react';
import { jsx, css } from '@emotion/core';
import { LayoutRenderer } from 'pretty-proptypes';
import md from 'react-markings';

import { gridSize } from '@atlaskit/theme/constants';
import {
  G50,
  G500,
  P50,
  P300,
  P500,
  N20,
  N30,
  N300,
  N800,
  R500,
} from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

/**
 * This gets imported in two places:
 *   1. Top-level component pages
 *   2. Sub-level component pages
 *
 * By having a wrapper we can have a single source of truth for both locations.
 */

const tableStyles = css`
  width: 100%;
  border-collapse: collapse;
  margin-top: 40px;

  th {
    text-align: left;
    padding: 4px 16px 4px 8px;
    white-space: nowrap;
    vertical-align: top;
  }

  td {
    padding: 4px 0 4px 8px;
    width: 100%;
  }

  tbody {
    border-bottom: none;
  }
`;

const expanderStyles = css`
  border-radius: 3px;
  border: 0;
  font-size: 14px;
  font-family: sans-serif;
  line-height: 20px;
  width: auto;
  margin: 2px 0;
  padding: 0 0.2em;
  :hover {
    cursor: pointer;
  }
`;

const typeStyles = css`
  background-color: ${token('color.background.discovery', P50)};
  border-radius: 3px;
  color: ${token('color.text.discovery', P500)};
  display: inline-block;
  margin: 2px 0;
  padding: 0 0.2em;
`;

const Type = (props: { children: ReactNode }) => (
  <span css={typeStyles} {...props} />
);

const typeMeta = css`
  background-color: ${token('color.background.neutral', N20)};
  color: ${token('color.text.subtle', N300)};
`;

const TypeMeta = (props: { children: ReactNode }) => (
  <span css={[typeStyles, typeMeta]} {...props} />
);

const stringType = css`
  background-color: ${token('color.background.success', G50)};
  color: ${token('color.text.success', G500)};
`;

const StringType = (props: { children: ReactNode }) => (
  <span css={[typeStyles, stringType]} {...props} />
);
const Required = (props: { children: ReactNode }) => (
  <span
    css={css`
      color: ${token('color.text.danger', R500)};
    `}
  >
    {props.children}
  </span>
);

const Expander = ({
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  children,
}: {
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    css={expanderStyles}
    style={{
      backgroundColor: isHovered
        ? token('color.background.discovery.hovered', P300)
        : token('color.background.neutral', N20),
      color: token('color.text.subtle', N300),
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </button>
);

const FunctionType = (props: { children: ReactNode }) => <span {...props} />;

const Props = (props) => (
  <div
    css={css`
      margin-top: 24px;
      margin-bottom: 48px;

      :last-child {
        margin-bottom: 0px;
      }

      > div {
        /* The wrapper from pretty-proptypes sets its own top margin */
        margin-top: 0;
        margin-bottom: 0;
      }

      h3 > code:last-of-type {
        /* Styles the 'required' prop text. Should be removed as soon as pretty-proptypes supports custom colours / tokens:
          https://product-fabric.atlassian.net/browse/DSP-3249 */
        color: ${token('color.text.danger', R500)};
      }

      h3 > code:first-of-type {
        /* Styles the prop name heading */
        background-color: ${token('color.background.neutral', N20)};
        color: ${token('color.text', N800)};
      }
    `}
  >
    <LayoutRenderer
      {...props}
      renderType={({
        name,
        required,
        defaultValue,
        description,
        type,
        components,
        typeValue,
      }) => (
        <table css={tableStyles}>
          <caption
            css={css`
              text-align: left;
              margin: 0;
              font-size: 1em;
            `}
          >
            <h3
              css={css`
                border-bottom: 1px solid ${token('color.border', N30)};
                font-size: 1em;
                font-weight: normal;
                line-height: 1.4;
                margin: 0 0 ${gridSize() / 2}px 0;
                padding-bottom: ${gridSize()}px;
              `}
            >
              <code
                css={css`
                  background-color: ${token('color.background.neutral', N20)};
                  border-radius: 3px;
                  color: ${token('color.text', N800)};
                  display: inline-block;
                  font-size: 1em;
                  line-height: 20px;
                  padding: 4px 8px;
                `}
              >
                {name}
              </code>
              {required && defaultValue === undefined && (
                <code
                  css={css`
                    margin-left: 1em;
                    color: ${token('color.text.danger', R500)};
                  `}
                >
                  required
                </code>
              )}
            </h3>
          </caption>
          <tbody>
            <tr>
              <th scope="row">Description</th>
              <td>
                {description && (
                  <div
                    css={css`
                      p:first-of-type {
                        margin-top: 0px;
                      }
                      p:last-of-type {
                        margin-bottom: 0px;
                      }
                    `}
                  >
                    {md([description])}
                  </div>
                )}
              </td>
            </tr>
            {defaultValue !== undefined && (
              <tr>
                <th scope="row">Default</th>
                <td>
                  <code
                    css={css`
                      color: ${token('color.text.subtle', N300)};
                    `}
                  >
                    {defaultValue}
                  </code>
                </td>
              </tr>
            )}
            <tr>
              <th scope="row">Type</th>
              <td
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <span>
                  <code
                    css={css`
                      background: ${token('color.background.neutral', N20)};
                      color: ${token('color.text.subtle', N300)};
                      border-radius: 3px;
                      display: inline-block;
                      padding: 0 0.2em;
                    `}
                  >
                    {type}
                  </code>
                </span>
                <span>
                  <components.PropType
                    typeValue={typeValue}
                    components={{
                      ...components,
                      Type,
                      TypeMeta,
                      StringType,
                      FunctionType,
                      Required,
                      Expander,
                    }}
                  />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    />
  </div>
);

export default Props;
